import React, { useState, useEffect, useCallback } from 'react';
import { DownloadCloud, Play, XCircle, RotateCcw, ChevronDown, ChevronRight, CheckCircle2, Loader2, Circle, FolderOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card, PageHeader, Button, Badge, EmptyState, Spinner } from '../components/ui';
import { backupsApi, platformConnectionsApi } from '../lib/api';
import type { BackupJob, PlatformConnection, ResourceTypeConfig } from '../types/vault';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

const statusTones: Record<string, 'success' | 'accent' | 'danger' | 'neutral' | 'warning'> = {
  completed: 'success',
  running: 'accent',
  failed: 'danger',
  cancelled: 'neutral',
  pending: 'warning',
};

export default function Backups() {
  const [jobs, setJobs] = useState<BackupJob[]>([]);
  const [connections, setConnections] = useState<PlatformConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showStartModal, setShowStartModal] = useState(false);
  const [selectedConnection, setSelectedConnection] = useState('');
  const [resourceTypes, setResourceTypes] = useState<ResourceTypeConfig[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [starting, setStarting] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [detail, setDetail] = useState<BackupJob | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (background = false) => {
    if (!background) setLoading(true);
    try {
      const [jobsRes, connRes] = await Promise.all([
        backupsApi.list(),
        platformConnectionsApi.list(),
      ]);
      if (jobsRes.success && jobsRes.data) setJobs(jobsRes.data.jobs);
      if (connRes.success && connRes.data) setConnections(connRes.data.connections.filter((c) => c.status === 'active'));
    } catch {
      if (!background) setError('Failed to load data');
    } finally {
      if (!background) setLoading(false);
    }
  };

  const loadDetail = useCallback(async (jobId: string) => {
    try {
      const res = await backupsApi.get(jobId);
      if (res.success && res.data) setDetail(res.data.job);
    } catch {
      /* detail is progressive enhancement — keep the row usable */
    }
  }, []);

  const toggleExpand = (jobId: string) => {
    if (expandedId === jobId) {
      setExpandedId(null);
      setDetail(null);
    } else {
      setExpandedId(jobId);
      setDetail(null);
      loadDetail(jobId);
    }
  };

  // Live progress: poll while any job is running (list + expanded detail)
  const anyRunning = jobs.some((j) => j.status === 'running' || j.status === 'pending');
  useEffect(() => {
    if (!anyRunning) return;
    const t = setInterval(() => {
      loadData(true);
      if (expandedId) loadDetail(expandedId);
    }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anyRunning, expandedId, loadDetail]);

  const handleConnectionChange = async (connId: string) => {
    setSelectedConnection(connId);
    setSelectedTypes([]);
    if (!connId) { setResourceTypes([]); return; }
    try {
      const res = await platformConnectionsApi.getResourceTypes(connId);
      if (res.success && res.data) setResourceTypes(res.data.resourceTypes);
    } catch {
      setResourceTypes([]);
    }
  };

  const toggleType = (type: string) => {
    setSelectedTypes((prev) =>
      prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
    );
  };

  const handleStart = async () => {
    if (!selectedConnection || selectedTypes.length === 0) return;
    setStarting(true);
    try {
      const res = await backupsApi.start(selectedConnection, selectedTypes);
      if (res.success) {
        setShowStartModal(false);
        setSelectedConnection('');
        setSelectedTypes([]);
        loadData();
      } else {
        setError(res.message || 'Failed to start backup');
      }
    } catch {
      setError('Failed to start backup');
    } finally {
      setStarting(false);
    }
  };

  const handleCancel = async (id: string) => {
    try {
      await backupsApi.cancel(id);
      loadData();
    } catch {
      setError('Failed to cancel backup');
    }
  };

  const [retryingId, setRetryingId] = useState<string | null>(null);
  const handleRetry = async (job: BackupJob) => {
    setRetryingId(job.id);
    try {
      const res = await backupsApi.start(job.connectionId, job.resourceTypes);
      if (res.success) {
        loadData();
      } else {
        setError(res.message || 'Failed to retry backup');
      }
    } catch {
      setError('Failed to retry backup');
    } finally {
      setRetryingId(null);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <PageHeader
          eyebrow="Backups"
          title="Backups"
          description="Run and monitor backup jobs"
          actions={
            <Button
              variant="primary"
              icon={Play}
              onClick={() => setShowStartModal(true)}
              disabled={connections.length === 0}
            >
              Start Backup
            </Button>
          }
        />

        {error && (
          <div className="bg-danger-soft border border-line text-danger px-4 py-3 rounded-[11px]">
            {error}
            <button onClick={() => setError(null)} className="ml-2 font-bold">&times;</button>
          </div>
        )}

        {loading ? (
          <div className="flex justify-center py-12">
            <Spinner />
          </div>
        ) : jobs.length === 0 ? (
          <EmptyState
            icon={DownloadCloud}
            title="No backup jobs yet"
            description="Start your first backup to protect your data."
          />
        ) : (
          <Card className="overflow-hidden">
            <table className="w-full">
              <thead className="bg-surface-2 border-b border-line">
                <tr>
                  <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Connection</th>
                  <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Status</th>
                  <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Progress</th>
                  <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Started</th>
                  <th className="text-left px-6 py-2.5 font-bold text-[10.5px] text-ink-3 uppercase tracking-wide">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <React.Fragment key={job.id}>
                  <tr
                    className="border-t border-line-2 hover:bg-surface-2 transition-colors cursor-pointer"
                    onClick={() => toggleExpand(job.id)}
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        {expandedId === job.id
                          ? <ChevronDown className="w-4 h-4 text-ink-3 flex-none" />
                          : <ChevronRight className="w-4 h-4 text-ink-3 flex-none" />}
                        <div>
                          <div className="font-medium text-ink">{job.connectionName || 'Unknown'}</div>
                          <div className="text-[13px] text-ink-3">{job.resourceTypes.join(', ')}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge tone={statusTones[job.status] || 'neutral'}>
                        {job.status}
                      </Badge>
                      {job.status === 'failed' && job.errorMessage && (
                        <div className="text-[12px] text-danger mt-1 max-w-[220px] truncate" title={job.errorMessage}>
                          {job.errorMessage}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 text-[13px] text-ink-2 font-mono">
                      {job.processedItems} / {job.totalItems || '?'} items
                      {job.failedItems > 0 && (
                        <span className="text-danger ml-1">({job.failedItems} failed)</span>
                      )}
                    </td>
                    <td className="px-6 py-4 text-[13px] text-ink-3 font-mono">
                      {job.startedAt ? new Date(job.startedAt).toLocaleString() : '-'}
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {(job.status === 'running' || job.status === 'pending') && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleCancel(job.id); }}
                            className="text-danger hover:opacity-80 text-sm"
                            title="Cancel backup"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                        {(job.status === 'failed' || job.status === 'cancelled' || job.status === 'completed') && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleRetry(job); }}
                            disabled={retryingId === job.id}
                            className="text-accent-ink hover:opacity-80 text-sm disabled:opacity-40 inline-flex items-center gap-1.5"
                            title={`Run this backup again (${job.resourceTypes.join(', ')})`}
                          >
                            <RotateCcw className={`w-4 h-4 ${retryingId === job.id ? 'animate-spin' : ''}`} />
                            <span className="text-[13px] font-semibold">Run again</span>
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  {expandedId === job.id && (
                    <tr className="border-t border-line-2 bg-surface-2/60">
                      <td colSpan={5} className="px-6 py-5">
                        {!detail || detail.id !== job.id ? (
                          <div className="flex items-center gap-2 text-[13px] text-ink-3">
                            <Loader2 className="w-4 h-4 animate-spin" /> Loading backup details…
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                              {(detail.typeProgress || []).map((tp) => (
                                <div key={tp.type} className="flex items-center gap-3 bg-surface border border-line rounded-[11px] px-4 py-3">
                                  {tp.state === 'completed' && <CheckCircle2 className="w-4 h-4 text-success flex-none" />}
                                  {tp.state === 'in_progress' && <Loader2 className="w-4 h-4 text-accent animate-spin flex-none" />}
                                  {tp.state === 'incomplete' && <Circle className="w-4 h-4 text-ink-3 flex-none" />}
                                  <div className="min-w-0">
                                    <div className="text-[13.5px] font-semibold text-ink capitalize">{tp.type}</div>
                                    <div className="text-[12.5px] text-ink-3 font-mono">
                                      {tp.items} items · {formatBytes(tp.bytes)}
                                      {tp.state === 'in_progress' && ' · backing up…'}
                                      {tp.state === 'incomplete' && ' · did not finish'}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                            {detail.errorMessage && (
                              <div className="text-[13px] text-danger bg-danger-soft border border-line rounded-[11px] px-4 py-3 font-mono">
                                Last error: {detail.errorMessage}
                              </div>
                            )}
                            <div className="flex items-center gap-4 text-[13px]">
                              {detail.status === 'completed' && detail.snapshotId && (
                                <Link
                                  to={`/vault/snapshots/${detail.snapshotId}/browse`}
                                  className="inline-flex items-center gap-1.5 font-semibold text-accent-ink hover:opacity-80"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <FolderOpen className="w-4 h-4" /> Browse this snapshot's data
                                </Link>
                              )}
                              <span className="text-ink-3 font-mono">
                                {detail.completedAt
                                  ? `Finished ${new Date(detail.completedAt).toLocaleString()}`
                                  : detail.status === 'running' ? 'In progress — updates every 5s' : ''}
                              </span>
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </Card>
        )}

        {/* Start Backup Modal */}
        {showStartModal && (
          <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
            <div className="ds-card ds-card-pad w-full max-w-md shadow-card animate-fade">
              <h2 className="font-display text-lg font-bold text-ink mb-4">Start Backup</h2>

              <div className="space-y-4">
                <div>
                  <label className="ds-label">Connection</label>
                  <select
                    value={selectedConnection}
                    onChange={(e) => handleConnectionChange(e.target.value)}
                    className="ds-input"
                  >
                    <option value="">Select a connection...</option>
                    {connections.map((c) => (
                      <option key={c.id} value={c.id}>{c.displayName} ({c.platform})</option>
                    ))}
                  </select>
                </div>

                {resourceTypes.length > 0 && (
                  <div>
                    <label className="ds-label">Resource Types</label>
                    <div className="space-y-2">
                      {resourceTypes.map((rt) => (
                        <label key={rt.type} className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={selectedTypes.includes(rt.type)}
                            onChange={() => toggleType(rt.type)}
                            className="rounded border-line text-accent focus:ring-accent-soft"
                          />
                          <span className="text-sm text-ink-2">{rt.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <Button variant="ghost" onClick={() => setShowStartModal(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleStart}
                  disabled={starting || !selectedConnection || selectedTypes.length === 0}
                >
                  {starting ? 'Starting...' : 'Start Backup'}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
